import { useSelection } from "@/contexts/SelectionContext";
import { useMergeSubQuizzes } from "@/lib/hooks/slides/useMergeSubQuizzes";
import { canAbsorbSlide, isFluencyReadingSlide } from "@/lib/utils/subQuizMerge";
import type { SlideFields } from "@/types/Slide";
import { Button, IconButton } from "@ai-teach/ui-web";
import { HiX } from "react-icons/hi";
import { MdMergeType } from "react-icons/md";

interface MergeModeActionBarProps {
  /** Loaded lesson slides — resolves the master (by id) and the selected sources. */
  slides: SlideFields[];
}

/**
 * Floating bar shown while in merge mode. The master is fixed (chosen when merge mode started); the
 * checked slides are the sources. Confirming absorbs them into the master's subQuizzes (and, for a
 * reading-fluency master, passages) and deletes them. Sources are ordered by `selectedSlides` — an
 * insertion-ordered array — not by list position, so the result matches the numbered badges the teacher
 * saw on the cards while selecting. Mirrors `SelectionActionModal`'s look, but with a single Merge
 * action.
 */
export function MergeModeActionBar({ slides }: MergeModeActionBarProps) {
  const { mergeMasterId, selectedSlides, exitMergeMode } = useSelection();
  const { mergeSubQuizzes, loading } = useMergeSubQuizzes();

  const master = slides.find((s) => s.id === mergeMasterId);

  // The master might not be in the currently loaded page (paginated list) — bail rather than guess.
  if (!master) return null;

  const masterIsFluencyReading = isFluencyReadingSlide(master);
  const sources = selectedSlides
    .map((item) => slides.find((s) => s.id === item.id))
    .filter((s): s is SlideFields => s !== undefined && canAbsorbSlide(s, masterIsFluencyReading));

  // Absorbing a reading-fluency slide concatenates its passages into the master permanently: there is
  // no per-source record afterward, so — unlike an ejected "other" quiz — this split can't be undone.
  const willPermanentlyMergePassages =
    masterIsFluencyReading && sources.some(isFluencyReadingSlide);

  const handleMerge = async () => {
    if (sources.length === 0) return;
    const ok = await mergeSubQuizzes(master, sources);
    if (ok) exitMergeMode();
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[800]">
      <div className="bg-surface rounded-lg shadow-2xl border border-gray-800 px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex flex-col">
            <span className="font-semibold text-secondary-200 max-w-xs truncate">
              Merging into “{master.name}”
            </span>
            <span className="text-xs text-gray-500">
              {sources.length} slide{sources.length === 1 ? "" : "s"} selected
            </span>
            {willPermanentlyMergePassages && (
              <span className="text-xs font-medium text-warning-500 max-w-xs">
                Passages will be combined permanently — this cannot be undone.
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 border-l border-gray-800 pl-6">
            <Button
              variant="primary"
              size="sm"
              onClick={handleMerge}
              disabled={sources.length === 0 || loading}
              className="flex items-center gap-2"
            >
              <MdMergeType className="w-4 h-4" />
              {loading ? "Merging..." : "Merge"}
            </Button>
          </div>

          <IconButton
            onClick={exitMergeMode}
            variant="ghost"
            shape="circle"
            aria-label="Cancel merge"
            disabled={loading}
          >
            <HiX className="w-5 h-5" />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
